/**
 * MessageReactions (#989)
 *
 * The reaction affordances under a chat bubble:
 *  - a row of {@link ReactionChip}s (existing reactions; tap to toggle your own,
 *    hover/focus to peek the reactor list),
 *  - an always-visible quick-picker trigger (#2902 — persistently discoverable,
 *    not hover-gated), shaped as a dashed "ghost chip" docked in the chip row
 *    (#2916, design option 3): same height/radius as the reaction chips, docked
 *    to the bubble edge — leading for others' messages, trailing for own — so
 *    it never floats detached at the far row edge.
 *
 * Self-contained: owns the optimistic toggle mutation ({@link useToggleReaction})
 * and the lazy reactor-list query ({@link useReactionUsers}). The action-sheet
 * palette row is a second, independent add-reaction entry point.
 *
 * Layer: Action (member-facing, one-tap). Layer-2 tokens only; Lucide `SmilePlus`
 * for the picker chrome; palette/chip glyphs are the Rule 20 carve-out content.
 */

import { useEffect, useRef, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import type { MessageReaction, ReactionEmoji } from '../lib/api/messages';
import { useToggleReaction, useReactionUsers } from '../hooks/useRoomMessages';
import ReactionChip from './ReactionChip';
import ReactionPalette from './ReactionPalette';

interface MessageReactionsProps {
  roomId: string;
  messageId: string;
  reactions: MessageReaction[];
  /** True when the viewer may react (room write access + a server-id message). */
  canReact: boolean;
  /** Right-align the row for own (self) messages. */
  alignEnd?: boolean;
}

// Hover-intent delay before opening a reactor popover (keyboard focus opens it
// immediately — no timer — so keyboard users and tests stay deterministic).
const HOVER_INTENT_MS = 300;
// Grace delay before closing on pointer-leave (George r1): the popover sits
// above the chip (`bottom-full`), so travelling the pointer up INTO it first
// leaves the chip. Without this delay that pointer-leave tears the popover down
// before the pointer arrives, making the scrollable reactor list unreachable.
// Entering the popover cancels the pending close (the hover bridge).
const HOVER_CLOSE_MS = 250;

export default function MessageReactions({
  roomId,
  messageId,
  reactions,
  canReact,
  alignEnd,
}: MessageReactionsProps) {
  const toggle = useToggleReaction(roomId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openReactorEmoji, setOpenReactorEmoji] = useState<ReactionEmoji | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  // #2906 review round: Safari and macOS Firefox do NOT move focus into a
  // <button> on mousedown, so a mouse click on a palette swatch (after opening
  // the picker via keyboard) blurs the trigger with relatedTarget null — the
  // focus-out close would tear the picker down BEFORE the click lands and the
  // reaction would silently never be added. A pointer-down inside the
  // disclosure sets this flag; the blur handler treats that as "interaction
  // within", not a departure.
  const pointerDownInsideRef = useRef(false);
  // Per-(message,emoji) in-flight state (#2873 item 1). `toggle.isPending` is a
  // single mutation-observer flag: any toggle on this row freezes EVERY chip.
  // Track a per-emoji ref-count instead so an unrelated chip stays interactive
  // while another is settling. A count (not a Set) is required because a key can
  // have two concurrent dispatches — the chain owner and a coalesced follow
  // (see reactionOptimism.ts) — and each pairs its own +1 with a -1 in the
  // `.finally` of ITS OWN `mutateAsync` promise (see handleToggle; per-call
  // `onSettled` is NOT reliable here — supersession detaches it, George r2). A
  // Set would clear on the FIRST settle while the owner's chain is still live.
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});
  const bumpPending = (emoji: ReactionEmoji, delta: number) =>
    setPendingCounts((prev) => {
      const next = (prev[emoji] ?? 0) + delta;
      const copy = { ...prev };
      if (next <= 0) delete copy[emoji];
      else copy[emoji] = next;
      return copy;
    });
  const isEmojiPending = (emoji: ReactionEmoji) => (pendingCounts[emoji] ?? 0) > 0;

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    [],
  );

  const reactorList = useReactionUsers(
    roomId,
    messageId,
    openReactorEmoji,
    openReactorEmoji !== null,
  );

  const mineSet = new Set<ReactionEmoji>(
    reactions.filter((r) => r.mine).map((r) => r.emoji),
  );

  const handleToggle = (emoji: ReactionEmoji) => {
    if (!canReact) return;
    const mine = reactions.find((r) => r.emoji === emoji)?.mine ?? false;
    bumpPending(emoji, 1);
    // The decrement MUST hang off THIS dispatch's own promise — NOT a per-call
    // `mutate(vars, { onSettled })` (Frank r1, verified in query-core 5.101.2).
    // `MutationObserver.mutate` stores the per-call callbacks in ONE private
    // slot and detaches itself from the previous mutation:
    //   `#mutateOptions = options; this.#currentMutation?.removeObserver(this)`
    // (`mutationObserver.js:39-41`). One observer serves this whole row, so a
    // second toggle (e.g. 🔥 while 👍 is still on the wire) overwrites the slot
    // AND unsubscribes from 👍's mutation — 👍's per-call `onSettled` never
    // fires, its count is stranded at 1, and that chip is disabled forever.
    // `mutateAsync` returns the promise of THIS `Mutation.execute`, which is
    // immune to supersession, so `.finally` runs exactly once per dispatch and
    // the ref-count always balances.
    //
    // The `.catch` only swallows the unhandled rejection: errors are still
    // toasted by useOroMutation's MUTATION-level `onError`, which runs inside
    // `Mutation.execute` (`mutation.js:159`) and is therefore unaffected by the
    // observer detach above.
    void toggle
      .mutateAsync({ messageId, emoji, mine })
      .catch(() => {})
      .finally(() => bumpPending(emoji, -1));
  };

  const cancelHoverTimer = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  };
  const openReactorSoon = (emoji: ReactionEmoji) => {
    cancelHoverTimer();
    hoverTimer.current = setTimeout(() => setOpenReactorEmoji(emoji), HOVER_INTENT_MS);
  };
  // Pointer-leave: close after a grace delay so the pointer can cross the gap
  // into the popover (which cancels it via cancelHoverTimer on enter).
  const closeReactorSoon = () => {
    cancelHoverTimer();
    hoverTimer.current = setTimeout(() => setOpenReactorEmoji(null), HOVER_CLOSE_MS);
  };
  // Blur (keyboard) closes immediately — no travel gap to bridge.
  const closeReactorNow = () => {
    cancelHoverTimer();
    setOpenReactorEmoji(null);
  };
  const openReactorNow = (emoji: ReactionEmoji) => {
    cancelHoverTimer();
    setOpenReactorEmoji(emoji);
  };

  const hasReactions = reactions.length > 0;
  // Empty + can't react → render nothing (zero chrome per the empty-state spec).
  if (!hasReactions && !canReact) return null;

  // #2916 (option 3): the add affordance is a dashed GHOST CHIP rendered inside
  // the chip row, docked to the bubble edge — leading for others' messages,
  // trailing for own (the row is `justify-end` for own, so the trailing item
  // hugs the bubble). Chip-shaped (same height/radius as ReactionChip) so it
  // reads as "one more chip slot", not a floating icon. Always visible (#2902 —
  // no hover-gating regression); disclosure behavior is unchanged.
  const addChip = canReact && (
    <span
      className="relative inline-flex"
      // George r3 parity (#2873 item 2): the quick-picker is a NON-MODAL
      // disclosure like the reactor peek — Escape dismisses it (no focus
      // trap, aria-modal="false") and returns focus to the trigger. Keydown
      // from the ghost chip OR any palette swatch bubbles here.
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !pickerOpen) return;
        // Consume it — same rationale as the reactor peek below (George r4).
        e.preventDefault();
        e.stopPropagation();
        setPickerOpen(false);
        addButtonRef.current?.focus();
      }}
      // #2906: close on focus-out — the standard non-modal dismissal. Without
      // a focus trap, a keyboard user can Tab beyond the disclosure, at which
      // point the span's Escape handler above is unreachable while pickerOpen
      // stays true. React's onBlur rides focusout (bubbles), so any focus
      // departure lands here; focus moving WITHIN the disclosure
      // (trigger ↔ swatches) has relatedTarget inside and is ignored. Focus is
      // NOT yanked back — the user is leaving deliberately. A pointer-down
      // inside the disclosure suppresses the close (see pointerDownInsideRef —
      // Safari/macOS-Firefox blur buttons to null on mousedown).
      onPointerDownCapture={() => {
        pointerDownInsideRef.current = true;
      }}
      onPointerUpCapture={() => {
        // Clear AFTER the click event this pointer-up produces has dispatched.
        setTimeout(() => {
          pointerDownInsideRef.current = false;
        }, 0);
      }}
      onBlur={(e) => {
        if (!pickerOpen) return;
        if (pointerDownInsideRef.current) return;
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setPickerOpen(false);
      }}
    >
      <button
        ref={addButtonRef}
        type="button"
        data-testid="reaction-add"
        aria-label="Add a reaction"
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        aria-controls={pickerOpen ? `reaction-quick-picker-${messageId}` : undefined}
        onClick={() => setPickerOpen((v) => !v)}
        // #2902: persistently visible on ALL breakpoints — never
        // `md:opacity-0 md:group-hover:opacity-100` (that hid it from
        // touch/keyboard users who never hover). Hover/focus still lift it.
        className="inline-flex items-center rounded-full border border-dashed border-oro-border-hover bg-transparent px-2 py-0.5 text-text-muted transition-colors hover:bg-oro-surface-alt hover:text-text-secondary focus-visible:text-text-secondary"
      >
        {/* opacity-60 (not the mock's ~45%): text-muted is already 60% alpha,
            so 0.60 × 0.60 ≈ 3.2:1 against the chat bg — the WCAG 1.4.11 3:1
            non-text floor; 45% composited to ~2.3:1 (review finding). */}
        <SmilePlus className="h-3.5 w-3.5 opacity-60" />
      </button>
      {pickerOpen && (
        <>
          {/* Click-away backdrop (transparent). */}
          <div
            className="fixed inset-0 z-30"
            aria-hidden="true"
            onClick={() => setPickerOpen(false)}
          />
          <div
            id={`reaction-quick-picker-${messageId}`}
            role="dialog"
            aria-modal="false"
            aria-label="Add a reaction"
            data-testid="reaction-quick-picker"
            className={`absolute bottom-full z-40 mb-1 rounded-xl border border-oro-border bg-oro-surface p-2 shadow-floating ${
              alignEnd ? 'right-0' : 'left-0'
            }`}
          >
            <ReactionPalette
              // #3056: fixed 6-column grid so this popover (which is
              // `absolute` inside the chip-width trigger span) sizes to the
              // palette's intrinsic width — a compact 2-row block — instead of
              // shrink-wrapping to the chip and wrapping to a one-emoji-wide,
              // full-height vertical strip. The action-sheet palette keeps the
              // default `wrap` layout (it renders full-width there).
              layout="grid"
              mine={mineSet}
              // Per-key, NOT `toggle.isPending` (Frank r1): the coarse flag
              // disabled all 12 swatches whenever ANY chip on this row was
              // settling — the exact row-wide freeze #2873 item 1 removes.
              isEmojiDisabled={isEmojiPending}
              onSelect={(emoji) => {
                handleToggle(emoji);
                setPickerOpen(false);
              }}
            />
          </div>
        </>
      )}
    </span>
  );

  return (
    <div
      data-testid="message-reactions"
      className={`mt-1 flex flex-wrap items-center gap-1 ${alignEnd ? 'justify-end' : ''}`}
    >
      {!alignEnd && addChip}
      {reactions.map((reaction) => (
        <span key={reaction.emoji} className="relative inline-flex">
          <ReactionChip
            reaction={reaction}
            // Disable ONLY for THIS emoji's in-flight state and ONLY when the
            // viewer can actually toggle (#2873 item 1 — per-key, not the whole
            // row). A read-only viewer (canReact=false) keeps an enabled,
            // non-toggleable chip so hover/focus can still peek the reactor list
            // (George r1) — the toggle is gated in handleToggle.
            disabled={canReact && isEmojiPending(reaction.emoji)}
            canToggle={canReact}
            popoverId={`reactor-popover-${reaction.emoji}`}
            popoverOpen={openReactorEmoji === reaction.emoji}
            onToggle={() => handleToggle(reaction.emoji)}
            onPointerEnter={() => openReactorSoon(reaction.emoji)}
            onPointerLeave={closeReactorSoon}
            onFocus={() => openReactorNow(reaction.emoji)}
            onBlur={closeReactorNow}
            onKeyDown={(e) => {
              // George r3: Escape closes the peek. This is a NON-MODAL disclosure
              // — focus stays on the chip (no focus trap, aria-modal="false");
              // Escape + blur are the dismissals, matching a hover/focus popover.
              if (e.key !== 'Escape' || openReactorEmoji !== reaction.emoji) return;
              // CONSUME it (George r4). Ancestor dismissables in this codebase all
              // listen at `document` in the bubble phase (`TopBar.tsx:129`,
              // `BottomSheet.tsx:35`, and 13 sibling modals). React 19 attaches its
              // root listener to the RTL/app root container, which sits BELOW
              // `document`, and synthetic `stopPropagation` forwards to the native
              // event — so stopping here prevents one Escape from dismissing both
              // this peek and whatever ancestor is also open ("local dismiss
              // first"). Only consumed on the branch we actually handle.
              e.preventDefault();
              e.stopPropagation();
              closeReactorNow();
            }}
          />
          {openReactorEmoji === reaction.emoji && (
            <div
              // Popover, not a tooltip (George r2): scrollable/interactive
              // content in a role="tooltip" is an a11y violation. role="dialog"
              // + aria-label; the chip advertises it via aria-haspopup/expanded.
              id={`reactor-popover-${reaction.emoji}`}
              role="dialog"
              // Explicitly NON-modal (George r4 L2): no focus trap, no inert
              // background. Some AT treat a bare role="dialog" as modal-ish and
              // clamp the virtual cursor to it; `false` is the honest signal.
              aria-modal="false"
              aria-label="Reactions"
              data-testid={`reactor-list-${reaction.emoji}`}
              onPointerEnter={cancelHoverTimer}
              onPointerLeave={closeReactorSoon}
              className="absolute bottom-full left-0 z-30 mb-1 max-h-48 w-max max-w-[16rem] overflow-y-auto rounded-lg border border-oro-border bg-oro-surface px-3 py-2 shadow-floating"
            >
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-text-muted">
                Reactions
              </p>
              {reactorList.isLoading ? (
                <p className="text-xs text-text-muted">Loading…</p>
              ) : reactorList.data && reactorList.data.users.length > 0 ? (
                <ul className="space-y-0.5">
                  {reactorList.data.users.map((u) => (
                    <li key={u.id} className="text-xs text-text-secondary">
                      {u.display_name}
                    </li>
                  ))}
                  {reactorList.data.has_more && (
                    <li className="text-[11px] text-text-muted">and more…</li>
                  )}
                </ul>
              ) : (
                <p className="text-xs text-text-muted">No one yet</p>
              )}
            </div>
          )}
        </span>
      ))}

      {alignEnd && addChip}
    </div>
  );
}
